/**
 * Checkout Page Component
 *
 * Alpine.js component for WooCommerce checkout page.
 * Handles form validation, payment method selection, and checkout interactions.
 *
 * @package Lovasok_Marketplace
 */

export default (config = {}) => ({
    // Cart items from PHP
    cartItems: config.cartItems || [],

    // Form state
    processing: false,
    errors: [],

    // Billing form validation
    billingValid: false,

    // Shipping toggle
    shipToDifferentAddress: false,

    // Payment method
    selectedPaymentMethod: '',

    // Totals (computed from cart)
    subtotal: 0,
    total: 0,

    // Initialization
    init() {
        console.log('Checkout page initialized', {
            cartItems: this.cartItems.length
        });

        // Calculate totals from cart items
        this.calculateTotals();

        // Listen to WooCommerce checkout events
        this.setupWooCommerceListeners();

        // Auto-select first payment method if available
        this.$nextTick(() => {
            const firstPaymentInput = document.querySelector('input[name="payment_method"]');
            if (firstPaymentInput) {
                this.selectedPaymentMethod = firstPaymentInput.value;
            }

            this.updatePaymentMethod();
            this.setupBillingStateDebounce();
        });
    },

    setupBillingStateDebounce() {
        const stateField = document.getElementById('billing_state');
        if (!stateField || typeof jQuery === 'undefined') {
            return;
        }

        let debounceTimer = null;
        jQuery(stateField).on('change input', () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                jQuery(document.body).trigger('update_checkout');
            }, 400);
        });
    },

    // Calculate totals
    calculateTotals() {
        this.subtotal = this.cartItems.reduce((sum, item) => sum + item.subtotal, 0);
        this.total = this.subtotal; // WooCommerce will handle actual total calculation
    },

    // Setup WooCommerce event listeners
    setupWooCommerceListeners() {
        // Listen for WooCommerce checkout updates
        jQuery(document.body).on('update_checkout', () => {
            console.log('WooCommerce checkout updating...');
        });

        jQuery(document.body).on('updated_checkout', () => {
            console.log('WooCommerce checkout updated');
            this.updatePaymentMethod();
        });

        // Listen for payment method changes
        jQuery(document.body).on('change', 'input[name="payment_method"]', (e) => {
            this.selectedPaymentMethod = e.target.value;
            console.log('Payment method changed:', this.selectedPaymentMethod);
        });

        // Listen for checkout errors
        jQuery(document.body).on('checkout_error', () => {
            this.processing = false;
            console.error('Checkout error occurred');

            // Scroll to error messages
            this.$nextTick(() => {
                const errorElement = document.querySelector('.woocommerce-error, .woocommerce-NoticeGroup-checkout');
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        });
    },

    // Update payment method selection
    updatePaymentMethod() {
        const selectedInput = document.querySelector('input[name="payment_method"]:checked');
        if (selectedInput) {
            this.selectedPaymentMethod = selectedInput.value;

            const buttonText = selectedInput.getAttribute('data-order_button_text');
            const placeOrder = document.getElementById('place_order');
            if (placeOrder && buttonText) {
                placeOrder.value = buttonText;
                placeOrder.textContent = buttonText;
            }
        }
    },

    // Validate billing form
    validateBillingForm() {
        const requiredFields = [
            'billing_first_name',
            'billing_last_name',
            'billing_email',
            'billing_phone'
        ];

        const errors = [];

        requiredFields.forEach(fieldName => {
            const field = document.querySelector(`[name="${fieldName}"]`);
            if (field && !field.value.trim()) {
                errors.push(`A mező kitöltése kötelező: ${field.placeholder || fieldName}`);
            }
        });

        // Email validation
        const emailField = document.querySelector('[name="billing_email"]');
        if (emailField && emailField.value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(emailField.value)) {
                errors.push('Érvénytelen email cím');
            }
        }

        this.errors = errors;
        this.billingValid = errors.length === 0;

        return this.billingValid;
    },

    // Handle form submission
    submitCheckout() {
        if (this.processing) return;

        // Validate form
        if (!this.validateBillingForm()) {
            // Scroll to first error
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Check if payment method is selected
        if (!this.selectedPaymentMethod) {
            alert('Kérlek válassz fizetési módot!');
            return;
        }

        this.processing = true;

        // Let WooCommerce handle the actual submission
        // The form will submit normally
    },

    // Format price
    formatPrice(price) {
        return new Intl.NumberFormat('hu-HU').format(price) + ' Ft';
    },

    // Handle ship to different address toggle
    toggleShippingAddress() {
        this.shipToDifferentAddress = !this.shipToDifferentAddress;

        // Trigger WooCommerce to show/hide shipping fields
        const checkbox = document.querySelector('#ship-to-different-address-checkbox');
        if (checkbox) {
            checkbox.checked = this.shipToDifferentAddress;
            jQuery(checkbox).trigger('change');
        }
    }
});
